import { dropHandler } from '@/utils/dragdrop';
import { Model, Ordenable } from '@/models/Model';
import { reorder } from '@/utils/list';
import { DropResult } from '@hello-pangea/dnd';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';

type BaseType = Model & Ordenable;

type UseOptimisticReorderProps<T extends BaseType, K extends keyof T = 'id'> = {
  items: T[];
  isReordering: boolean;
  onReorder: (keys: T[K][]) => void;
  key?: K;
};

const useOptimisticReorder = <T extends BaseType, K extends keyof T = 'id'>({
  items: originalItems,
  isReordering,
  onReorder,
  key = 'id' as K,
}: UseOptimisticReorderProps<T, K>) => {
  const [localItems, setLocalItems] = useState<T[]>(originalItems);

  useEffect(() => {
    if (isReordering) return;
    setLocalItems(prev => (isEqual(prev, originalItems) ? prev : originalItems));
  }, [originalItems, isReordering]);

  const handleDragEndLocal = (result: DropResult) => {
    const reordered = dropHandler(result, localItems);
    if (reordered === localItems) return;
    const reorderedKeys = reordered.map(item => item[key]);
    const newLocalItems = reorder(localItems, reorderedKeys, key);
    setLocalItems(newLocalItems);
    onReorder(reorderedKeys);
  };

  return {
    optimistic: localItems,
    handleDragEnd: handleDragEndLocal,
  };
};

export default useOptimisticReorder;
